import request from "utils/request";
import { baseRequestURL } from "api/BaseAPI";
import { AibleClientFactory } from "client/aible/factory";
import { uploadFile } from "client/customer/dataset";
import { getOutputContext } from "@/utils/relation";
import { getSchemaColumns } from "@/utils/expression";
function getURLSearchParamsFromOptions(options) {
    const { folderId, next, latestId, fetchAll, query, datasetIds } = options;
    const searchParams = new URLSearchParams("");
    if (folderId) {
        searchParams.append("folder_id", folderId.toString());
    }
    if (!fetchAll) {
        searchParams.append("order_by", "id");
        searchParams.append("reverse", "1");
        if (next) {
            searchParams.append("next", next.toString());
        }
        if (latestId !== undefined && latestId !== null) {
            searchParams.append("id", latestId.toString());
        }
    }
    if (query) {
        searchParams.append("query", query.toString());
    }
    if (datasetIds) {
        for (const dId of datasetIds) {
            searchParams.append(".id", dId.toString());
        }
    }
    return searchParams;
}
export function getDatasets(options) {
    const searchParams = getURLSearchParamsFromOptions(options);
    return request(`${baseRequestURL}v1/data?${searchParams.toString()}`, {
        method: "GET"
    }).then((response) => response);
}
export function getDatasetsThin(options, include) {
    const searchParams = getURLSearchParamsFromOptions(options);
    for (const inc of include) {
        searchParams.append("i", inc);
    }
    return request(`${baseRequestURL}v1/data?${searchParams.toString()}`, {
        method: "GET"
    });
}
export function getDatasetsById(datasetIds, folderId) {
    return getDatasets({
        fetchAll: true,
        datasetIds: datasetIds,
        folderId: folderId
    });
}
export function getDataset(id) {
    return request(baseRequestURL + "v1/data/" + id, {
        method: "GET"
    }).then((response) => response);
}
export function updateDataset(id, update) {
    return request(baseRequestURL + "v1/data/" + id, {
        method: "PATCH",
        body: JSON.stringify(update)
    }).then((response) => response);
}
export function deleteDataset(id) {
    return request(baseRequestURL + "v1/data/" + id, {
        method: "DELETE"
    }).then((response) => response);
}
export function startRulefit(id, success) {
    return request(baseRequestURL + "v1/data/" + id + "/rulefits", {
        method: "POST",
        body: JSON.stringify({
            outcome_value: [success]
        })
    }).then((response) => response);
}
export function updateColumnDescriptions(id, descriptions) {
    return request(baseRequestURL + "v1/data/" + id + "/descriptions", {
        method: "PATCH",
        body: JSON.stringify({
            columns: descriptions
        })
    }).then((response) => response);
}
export function updateTransformations(id, transformations) {
    return updateDataset(id, {
        transformations: transformations,
        reindex: true
    });
}
export function updateOptions(id, options, reindex = true) {
    return updateDataset(id, {
        options: options,
        reindex: reindex
    });
}
export function updateReIndex(id) {
    return updateDataset(id, {
        reindex: true
    });
}
export function moveDatasetFolder(id, folderId) {
    return request(baseRequestURL + "v1/folders/" + folderId, {
        method: "POST",
        body: JSON.stringify({
            training_data_ids: [id]
        })
    }).then((response) => response);
}
export function updateTargetValue(id, targetValue) {
    return updateDataset(id, {
        target_value: [targetValue],
        reindex: true
    });
}
// selects the correct flow for uploads depending on the account type
export function uploadDataset(file, { target, folderId, accountId, options, ...otherOptions }) {
    const datasetClient = AibleClientFactory.getInstance().getDatasetClient();
    return datasetClient.uploadDataset(file, folderId, accountId, otherOptions, target, options);
}
export function linkDataset(file, name, folderId, accountId, uploadName) {
    const datasetClient = AibleClientFactory.getInstance().getDatasetClient();
    return datasetClient.linkDataset(file, name, folderId, accountId, uploadName);
}
export function finalizeDataset(backendUpdate) {
    return request(baseRequestURL + "v1/data", {
        method: "POST",
        body: JSON.stringify(backendUpdate)
    });
}
export function renameDataset(datasetId, name) {
    return updateDataset(datasetId, {
        name: name?.replace(/(\r\n|\n|\r)/gm, "")
    });
}
export function setTarget(datasetId, target, success, options) {
    const update = { target, options };
    update.target = target?.replace(/(\r\n|\n|\r)/gm, "");
    update.reindex = true;
    if (success !== undefined) {
        update.target_value = [success];
    }
    if (update.options !== undefined && update.options.target_model_params?.data?.summary_detail == "low") {
        const dataOptions = {
            ...update.options.target_model_params?.data
        };
        delete dataOptions.summary_detail;
        update.options = {
            ...update.options,
            target_model_params: {
                ...update.options.target_model_params,
                data: dataOptions
            }
        };
    }
    return updateDataset(datasetId, update);
}
export function setNewTarget(datasetId, target, options) {
    const update = {
        new_target: target?.replace(/(\r\n|\n|\r)/gm, ""),
        options
    };
    if (update.options !== undefined && update.options.target_model_params?.data?.summary_detail == "low") {
        const dataOptions = {
            ...update.options.target_model_params?.data
        };
        delete dataOptions.summary_detail;
        update.options = {
            ...update.options,
            target_model_params: {
                ...update.options.target_model_params,
                data: dataOptions
            }
        };
    }
    return updateDataset(datasetId, update);
}
export function reindex(datasetId) {
    return request(baseRequestURL + "v1/data/" + datasetId + "/reindex", {
        method: "POST"
    }).then((response) => response);
}
export function copyAndUpdate(dataset, updates) {
    // If no target is selected, do not include the labels
    const retTarget = updates.target ?? dataset.target;
    const retLabels = updates.labels ?? dataset.labels;
    const includeLabels = retTarget !== undefined && retTarget !== null;
    const payload = {
        url: dataset.source.url,
        source_content: dataset.source.content,
        schema_monitor_id: dataset.schema_monitor_id,
        account_id: dataset.account_id,
        name: dataset.name,
        folder_id: dataset.folder_id,
        target: retTarget,
        file_size: dataset.file_size,
        transformations: dataset.transformations,
        options: dataset.options,
        ...updates,
        labels: includeLabels ? retLabels : undefined
    };
    if (payload.target !== undefined &&
        payload.target !== null &&
        payload.labels !== undefined &&
        !payload.labels.some((l) => l === payload.target)) {
        payload.labels = payload.labels.concat([payload.target]);
    }
    if (payload.target !== undefined &&
        payload.options !== undefined &&
        payload.options.target_model_params?.data?.summary_detail == "low") {
        const dataOptions = {
            ...payload.options.target_model_params?.data
        };
        delete dataOptions.summary_detail;
        payload.options = {
            ...payload.options,
            target_model_params: {
                ...payload.options.target_model_params,
                data: dataOptions
            }
        };
    }
    return request(baseRequestURL + "v1/data/", {
        method: "POST",
        body: JSON.stringify(payload)
    }).then((response) => response);
}
export function copyAndTransform(dataset, transformations, updatedName, updatedTarget, updatedSuccess, labels, folderIdOverride) {
    // If no target is selected, do not include the labels
    const retTarget = updatedTarget ?? dataset.target;
    const retLabels = labels ?? dataset.labels;
    const includeLabels = retTarget !== undefined && retTarget !== null;
    const payload = {
        url: dataset.source.url,
        source_content: dataset.source.content,
        schema_monitor_id: dataset.schema_monitor_id,
        account_id: dataset.account_id,
        name: updatedName ?? dataset.name,
        folder_id: folderIdOverride ?? dataset.folder_id,
        labels: includeLabels ? retLabels : undefined,
        //descriptions: dataset.descriptions,
        target: retTarget,
        file_size: dataset.file_size,
        transformations: transformations ?? dataset.transformations,
        options: dataset.options
    };
    const relation = payload.transformations?.plan?.relation;
    if (relation !== undefined) {
        const labels = getSchemaColumns(getOutputContext(relation));
        if (payload.options?.kpi_analysis?.date_column !== undefined &&
            !labels.includes(payload.options.kpi_analysis.date_column)) {
            const { kpi_analysis, ...updatedOptions } = payload.options;
            payload.options = updatedOptions;
        }
        if (payload.options?.timestamp_column !== undefined && !labels.includes(payload.options?.timestamp_column)) {
            const { timestamp_column, ...updatedOptions } = payload.options;
            payload.options = updatedOptions;
        }
    }
    if (updatedSuccess !== undefined) {
        payload.target_value = [updatedSuccess];
    }
    if (payload.target !== undefined &&
        payload.target !== null &&
        payload.labels !== undefined &&
        !payload.labels.some((l) => l === payload.target)) {
        payload.labels = payload.labels.concat([payload.target]);
    }
    if (payload.target !== undefined &&
        payload.options !== undefined &&
        payload.options.target_model_params?.data?.summary_detail == "low") {
        const dataOptions = {
            ...payload.options.target_model_params?.data
        };
        delete dataOptions.summary_detail;
        payload.options = {
            ...payload.options,
            target_model_params: {
                ...payload.options.target_model_params,
                data: dataOptions
            }
        };
    }
    return request(baseRequestURL + "v1/data/", {
        method: "POST",
        body: JSON.stringify(payload)
    }).then((response) => response);
}
export function getExternalModelURL(datasetId) {
    return request(baseRequestURL + "v1/data/" + datasetId + "/external_model_presigned_url", {
        method: "GET"
    }).then((response) => response);
}
export function externalModelUrl(response, name, displayName, URL, username, password) {
    const payload = [
        {
            name: name,
            display_name: displayName,
            url: URL,
            username: username,
            password: password
        }
    ];
    return uploadFile(JSON.stringify(payload), response);
}
export function getAPIBaseURL() {
    return baseRequestURL;
}
export function uploadAsset(accountId, file, name) {
    const accountClient = AibleClientFactory.getInstance().getAccountClient();
    return accountClient.getCustomerClientFactory(accountId).then((factory) => {
        const cloudDatasetClient = factory.getDatasetClient();
        return accountClient
            .getPresignedUrl(accountId, name)
            .then((upload) => cloudDatasetClient.uploadDataset(file, upload));
    });
}
export function getDataSourceSummaries(folderId) {
    return request(baseRequestURL + "v1/folders/" + folderId + "/source_summaries", {
        method: "GET"
    }).then((r) => r);
}
